import Endpoint, { EndpointConfig } from "./Endpoint";
import * as Requester from "./Requester";
import {
	BaseRequestConfig,
	Body,
	Params,
	Query,
	RequestConfig,
	RequestEventHandlers,
	RequestHost,
	RequestMethod,
	RequestMiddleware,
	ResponseType,
} from "./ApiTypes";
import { EndpointMockingInfo, MockingConfig } from "./Mocking";
import * as StorageUtils from "../util/StorageUtils";
import { ModulePossiblyDefault } from "../types/UtilTypes";

export interface ApiInfo {
	readonly name: string;
	readonly baseUrl: string;
	readonly middleware?: RequestMiddleware[];
	readonly defaults?: BaseRequestConfig | (() => BaseRequestConfig);
	readonly mocking?: MockingConfig;
}

export interface ApiMocking extends MockingConfig {
	loaderPromise: null | Promise<ModulePossiblyDefault<any>>;
	loaded: boolean;
}

class HotRequestHost implements RequestHost {
	readonly api: ApiDef;
	readonly method: RequestMethod;
	readonly path: string;
	readonly responseType = ResponseType.Json;

	constructor(api: ApiDef, path: string, method: RequestMethod) {
		this.api = api;
		this.method = method;
		this.path = path;
	}

	get baseUrl(): string {
		return this.api.baseUrl;
	}

	computeConfig<
		R,
		P extends Params | undefined,
		Q extends Query | undefined,
		B extends Body | undefined,
	>(config: RequestConfig<R, P, Q, B>): RequestConfig<R, P, Q, B> {
		const apiDefaults = this.api.getDefaults();

		const computedConfig: RequestConfig<R, P, Q, B> = Object.assign(
			{},
			apiDefaults || {},
			config,
		);
		computedConfig.options = Object.assign(
			{},
			apiDefaults.options || {},
			config?.options || {},
		);
		return computedConfig;
	}

	computePath(path: string, config: RequestConfig): string {
		return path.startsWith("/") ? path : `/${path}`;
	}
}

export default class ApiDef implements Omit<ApiInfo, "defaults"> {
	readonly baseUrl: string;
	readonly name: string;
	readonly middleware: RequestMiddleware[];
	private readonly defaults: BaseRequestConfig | (() => BaseRequestConfig);
	readonly mocking: ApiMocking | undefined;

	private readonly endpoints: Record<string, Endpoint> = {};

	constructor(info: ApiInfo) {
		this.name = info.name;
		this.baseUrl = info.baseUrl;
		this.middleware = info.middleware || [];
		this.endpoints = {};
		this.defaults = info.defaults || {};
		this.mocking =
			info.mocking &&
			Object.assign(info.mocking, {
				loaderPromise: null,
				loaded: false,
			});
	}

	getDefaults(): BaseRequestConfig {
		return (
			(typeof this.defaults === "function" ? this.defaults() : this.defaults) ||
			{}
		);
	}

	endpoint<
		R,
		P extends Params | undefined = undefined,
		Q extends Query | undefined = undefined,
		B extends Body | undefined = undefined,
	>(info: EndpointConfig<R, P, Q, B>): Endpoint<R, P, Q, B> {
		const endpoint = new Endpoint<R, P, Q, B>(this, info);
		this.endpoints[endpoint.id] = endpoint as Endpoint;
		return endpoint;
	}

	getEventHandlers(): RequestEventHandlers<any> {
		return {};
	}

	async get(path: string, config: RequestConfig) {
		return await Requester.submit(
			new HotRequestHost(this, path, RequestMethod.Get) as any,
			config as any,
		);
	}

	async post(path: string, config: RequestConfig) {
		return await Requester.submit(
			new HotRequestHost(this, path, RequestMethod.Post) as any,
			config as any,
		);
	}

	clearCache(regex?: RegExp) {
		const regexs = [new RegExp("(REQUEST)")];
		if (regex) regexs.push(regex);
		return StorageUtils.clearRegex(regexs, { session: true });
	}

	getEndpointMocks(): Record<string, EndpointMockingInfo | null> {
		return Object.keys(this.endpoints).reduce((mocks, key) => {
			(mocks as any)[key] = this.endpoints[key].mocking;
			return mocks;
		}, {});
	}
}
